import {
  loadSession,
  authHeaders,
  getOrCreateDeviceId,
  extractVapidKey,
} from './gatewayAuth'
import {
  loadControllerSession,
  authHeadersController,
} from './controllerAuth'

/**
 * Convierte una VAPID key URL-safe base64 a Uint8Array.
 * @param {string} base64String
 * @returns {Uint8Array}
 */
export function urlBase64ToUint8Array(base64String) {
  const raw = String(base64String || '').trim()
  if (!raw) {
    throw new Error('VAPID key vacía')
  }
  const padding = '='.repeat((4 - (raw.length % 4)) % 4)
  const base64 = (raw + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

/**
 * Body de POST /api/v1/push/subscribe (Controller: keys nested; Edge: flat + nested).
 * @param {string} deviceId
 * @param {PushSubscription | { endpoint?: string, keys?: object, toJSON?: Function }} subscription
 */
export function buildSubscribeBody(deviceId, subscription) {
  const json =
    subscription && typeof subscription.toJSON === 'function'
      ? subscription.toJSON()
      : subscription || {}
  const keys = json.keys || {}
  const p256dh = keys.p256dh
  const auth = keys.auth
  return {
    device_id: deviceId,
    platform: 'pwa',
    endpoint: json.endpoint || subscription.endpoint,
    p256dh,
    auth,
    keys: {
      p256dh,
      auth,
    },
  }
}

export function assertPushSupported() {
  if (typeof window === 'undefined') {
    throw new Error('Push no disponible fuera del navegador')
  }
  if (typeof window.isSecureContext === 'boolean' && !window.isSecureContext) {
    throw new Error('Web Push requiere HTTPS o localhost')
  }
  if (!('Notification' in window)) {
    throw new Error('Este navegador no soporta notificaciones')
  }
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    throw new Error('Service Worker no disponible')
  }
  if (!('PushManager' in window)) {
    throw new Error(
      'Push no disponible. En iOS, instale la PWA en la pantalla de inicio (iOS 16.4+).'
    )
  }
}

async function ensureServiceWorkerRegistration() {
  if (!navigator.serviceWorker || !navigator.serviceWorker.getRegistration) {
    throw new Error(
      'Push requiere build de producción (npm run serve / PWA instalada). El SW no está activo en npm start.'
    )
  }
  const existing = await navigator.serviceWorker.getRegistration()
  if (!existing) {
    throw new Error(
      'Push requiere build de producción (npm run serve / PWA instalada). El SW no está activo en npm start.'
    )
  }
  const registration = await navigator.serviceWorker.ready
  if (!registration || !registration.pushManager) {
    throw new Error(
      'Push requiere build de producción (npm run serve / PWA instalada). El SW no está activo en npm start.'
    )
  }
  return registration
}

async function fetchVapidKey(serverUrl, headers, fromLogin) {
  if (fromLogin) return String(fromLogin).trim()
  const res = await fetch(`${serverUrl}/api/v1/push/vapid-key`, {
    method: 'GET',
    headers,
  })
  if (!res.ok) {
    throw new Error(`No se pudo obtener la VAPID key (HTTP ${res.status})`)
  }
  const contentType =
    (res.headers && res.headers.get && res.headers.get('content-type')) || ''
  if (contentType.indexOf('application/json') !== -1) {
    const data = await res.json()
    const key = extractVapidKey(data)
    if (!key) {
      throw new Error('Respuesta VAPID sin clave pública')
    }
    return key
  }
  const text = String(await res.text()).trim()
  if (!text) {
    throw new Error('Respuesta VAPID vacía')
  }
  if (text.charAt(0) === '{' || text.charAt(0) === '"') {
    try {
      const key = extractVapidKey(JSON.parse(text))
      if (key) return key
    } catch (e) {
      /* usar texto crudo */
    }
  }
  return text
}

/**
 * Resuelve destino Push: Controller si hay JWT Controller; si no Edge legacy.
 * @param {{ session?: object, controllerSession?: object, preferController?: boolean }} opts
 */
function resolvePushTarget(opts) {
  const o = opts || {}
  if (o.preferController === false && o.session) {
    return {
      serverUrl: o.session.serverUrl,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(o.session),
      },
      kind: 'edge',
    }
  }
  const ctrl = o.controllerSession || loadControllerSession()
  if (ctrl && ctrl.token) {
    return {
      serverUrl: ctrl.serverUrl,
      headers: {
        'Content-Type': 'application/json',
        ...authHeadersController(ctrl),
      },
      kind: 'controller',
    }
  }
  const session = o.session || loadSession()
  if (session && session.token) {
    return {
      serverUrl: session.serverUrl,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(session),
      },
      kind: 'edge',
    }
  }
  return null
}

/**
 * Permiso nativo + subscribe PushManager + POST Controller o Edge.
 * @param {{ session?: object, controllerSession?: object, vapidPublicKey?: string, preferController?: boolean }} [options]
 */
export async function subscribeToPush(options) {
  const opts = options || {}
  const target = resolvePushTarget(opts)
  if (!target) {
    throw new Error(
      'No hay sesión de Controller ni de Gateway. Inicie sesión primero.'
    )
  }

  assertPushSupported()
  const registration = await ensureServiceWorkerRegistration()

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    throw new Error('Permiso de notificaciones denegado')
  }

  const vapidPublicKey = await fetchVapidKey(
    target.serverUrl,
    target.headers,
    opts.vapidPublicKey
  )

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
  })

  const body = buildSubscribeBody(getOrCreateDeviceId(), subscription)
  const res = await fetch(`${target.serverUrl}/api/v1/push/subscribe`, {
    method: 'POST',
    headers: target.headers,
    body: JSON.stringify(body),
  })

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(
      `Suscripción Push falló (HTTP ${res.status})${text ? `: ${text.slice(0, 200)}` : ''}`
    )
  }

  return { subscription, body, target: target.kind }
}
